import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  Users,
  CheckCircle,
  AlertTriangle,
  FileText,
  Loader2,
  Star,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { renderSafeHtml, stripHtml } from '../components/RichTextEditor';

const ExamResults = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { token, isTeacher } = useAuth();
  const [exam, setExam] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStudents, setExpandedStudents] = useState({});
  const [detailAttempt, setDetailAttempt] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isTeacher) {
      navigate('/teacher/login');
      return;
    }
    fetchResults();
  }, [isTeacher, examId]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/results/exam/${examId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setExam(data.exam);
        setSessions(data.sessions);
      } else {
        toast.error('فشل تحميل النتائج');
        navigate('/teacher/dashboard');
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('حدث خطأ أثناء تحميل النتائج');
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">مكتمل</Badge>;
      case 'terminated':
        return <Badge variant="destructive">منتهي بمخالفة</Badge>;
      case 'in_progress':
        return <Badge variant="secondary">قيد التنفيذ</Badge>;
      case 'blocked':
        return <Badge className="bg-orange-100 text-orange-800">محجوب</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Group sessions by student, mark first attempt
  const groupedByStudent = sessions.reduce((acc, session) => {
    const sid = session.student_id;
    if (!acc[sid]) {
      acc[sid] = { student_id: sid, student_name: session.student_name, attempts: [] };
    }
    acc[sid].attempts.push(session);
    return acc;
  }, {});

  // Sort attempts per student by attempt_number, mark first completed
  Object.values(groupedByStudent).forEach((group) => {
    group.attempts.sort((a, b) => (a.attempt_number || 1) - (b.attempt_number || 1));
    const firstCompleted = group.attempts.find(
      (a) => a.status === 'completed' || a.status === 'terminated' || a.status === 'blocked'
    );
    if (firstCompleted) firstCompleted.is_first_attempt = true;
  });

  const studentGroups = Object.values(groupedByStudent);

  const calculateStats = () => {
    // Use only first attempts for stats
    const firstAttempts = studentGroups
      .map((g) => g.attempts.find((a) => a.is_first_attempt))
      .filter(Boolean);
    if (firstAttempts.length === 0) return { avg: 0, max: 0, min: 0, passed: 0 };
    const scores = firstAttempts.map((s) => s.score || 0);
    const totalPoints = exam?.total_points || 1;
    const passScore = totalPoints * 0.5;
    return {
      avg: (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1),
      max: Math.max(...scores),
      min: Math.min(...scores),
      passed: firstAttempts.filter((s) => (s.score || 0) >= passScore).length,
      total: firstAttempts.length,
    };
  };

  const toggleStudent = (sid) => {
    setExpandedStudents((prev) => ({ ...prev, [sid]: !prev[sid] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#d4a853]" />
      </div>
    );
  }

  const stats = calculateStats();
  const completedCount = sessions.filter(
    (s) => s.status === 'completed' || s.status === 'terminated'
  ).length;
  const violationsCount = sessions.filter((s) => s.status === 'terminated').length;

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="bg-[#FFFFFF]/80 border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/teacher/dashboard" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#d4a853]/30" />
              <span className="text-xl font-bold text-[#d4a853]">المعتمد في اللغة العربية</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/teacher/dashboard')}
            className="text-[#6B4C2A] hover:text-[#412402] mb-4"
          >
            <ArrowRight className="w-4 h-4 ml-2" />
            العودة للوحة التحكم
          </Button>
          <h1 className="text-2xl font-bold text-[#412402]">{exam?.title}</h1>
          <p className="text-[#6B4C2A] mt-1">نتائج الاختبار</p>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-[#E8DFCF]">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6B4C2A]">عدد المشاركين</p>
                <p className="text-3xl font-bold text-[#412402]">{stats.total || 0}</p>
                <p className="text-xs text-[#8B7355]">{completedCount} جلسة إجمالاً</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-700" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E8DFCF]">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6B4C2A]">متوسط الدرجات</p>
                <p className="text-3xl font-bold text-[#412402]">{stats.avg}</p>
                <p className="text-xs text-[#8B7355]">من {exam?.total_points} (المحاولة الأولى)</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-green-700" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E8DFCF]">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6B4C2A]">الناجحون (50%+)</p>
                <p className="text-3xl font-bold text-[#412402]">{stats.passed}</p>
                <p className="text-xs text-[#8B7355]">من {stats.total || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[#d4a853]/15 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#d4a853]" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E8DFCF]">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6B4C2A]">المخالفات</p>
                <p className="text-3xl font-bold text-red-600">{violationsCount}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-700" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results — grouped by student */}
        <Card className="border-[#E8DFCF]">
          <CardHeader className="border-b border-[#E8DFCF]">
            <CardTitle className="text-lg font-semibold text-[#412402]">
              نتائج الطلاب
              <span className="text-sm font-normal text-[#412402]/50 mr-2">
                — الدرجة المعتمدة هي المحاولة الأولى
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {studentGroups.length === 0 ? (
              <div className="py-12 text-center">
                <Users className="w-12 h-12 text-[#E8DFCF] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#412402] mb-2">لا توجد نتائج بعد</h3>
                <p className="text-[#8B7355]">لم يقم أي طالب بإجراء هذا الاختبار حتى الآن</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E8DFCF]">
                {studentGroups.map((group) => {
                  const firstAttempt = group.attempts.find((a) => a.is_first_attempt);
                  const hasMultiple = group.attempts.length > 1;
                  const isExpanded = expandedStudents[group.student_id];
                  const pct = exam?.total_points && firstAttempt
                    ? ((firstAttempt.score || 0) / exam.total_points * 100).toFixed(0)
                    : 0;
                  const passed = pct >= 50;

                  return (
                    <div key={group.student_id} data-testid={`student-group-${group.student_id}`}>
                      {/* Student row — first attempt = official */}
                      <div
                        className={`flex items-center gap-4 px-4 py-4 hover:bg-[#FAFAFA] ${hasMultiple ? 'cursor-pointer' : ''}`}
                        onClick={() => hasMultiple && toggleStudent(group.student_id)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-[#412402]">{group.student_name}</p>
                            {hasMultiple && (
                              <Badge variant="outline" className="text-xs">
                                {group.attempts.length} محاولات
                              </Badge>
                            )}
                          </div>
                          {firstAttempt && (
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {getStatusBadge(firstAttempt.status)}
                              <span className="text-xs text-[#412402]/50">
                                {new Date(firstAttempt.started_at).toLocaleString('ar-SA')}
                              </span>
                            </div>
                          )}
                        </div>

                        {firstAttempt && (
                          <div className="flex items-center gap-3">
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                <Star className="w-3 h-3 text-amber-500" />
                                <span className={`text-lg font-bold ${passed ? 'text-green-600' : 'text-red-600'}`}>
                                  {firstAttempt.score || 0}
                                </span>
                                <span className="text-[#8B7355]">/ {exam?.total_points}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {pct}%
                                </span>
                              </div>
                              <p className="text-xs text-[#412402]/40 text-right">الدرجة الرسمية</p>
                            </div>
                            {firstAttempt.violation_count > 0 && (
                              <Badge variant="destructive">{firstAttempt.violation_count}</Badge>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-7"
                              onClick={(e) => { e.stopPropagation(); setDetailAttempt({ ...firstAttempt, student_name: group.student_name }); }}
                              data-testid={`view-student-detail-${group.student_id}`}
                            >
                              تفاصيل
                            </Button>
                            {hasMultiple && (
                              isExpanded
                                ? <ChevronUp className="w-4 h-4 text-[#412402]/40" />
                                : <ChevronDown className="w-4 h-4 text-[#412402]/40" />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Expanded: all attempts */}
                      {hasMultiple && isExpanded && (
                        <div className="bg-[#FAFAFA] border-t border-[#E8DFCF]">
                          <div className="px-8 py-2">
                            <p className="text-xs text-[#412402]/50 mb-2">جميع المحاولات:</p>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-xs text-[#412402]/50 border-b border-[#E8DFCF]">
                                  <th className="pb-1 text-right">المحاولة</th>
                                  <th className="pb-1 text-right">الحالة</th>
                                  <th className="pb-1 text-right">الدرجة</th>
                                  <th className="pb-1 text-right">المخالفات</th>
                                  <th className="pb-1 text-right">التاريخ</th>
                                  <th className="pb-1 text-right">تفاصيل</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.attempts.map((attempt) => {
                                  const aPct = exam?.total_points
                                    ? ((attempt.score || 0) / exam.total_points * 100).toFixed(0)
                                    : 0;
                                  return (
                                    <tr key={attempt.id} className="border-b border-[#E8DFCF] last:border-0">
                                      <td className="py-2">
                                        <div className="flex items-center gap-1">
                                          <span>#{attempt.attempt_number || 1}</span>
                                          {attempt.is_first_attempt && (
                                            <Star className="w-3 h-3 text-amber-500" />
                                          )}
                                        </div>
                                      </td>
                                      <td className="py-2">{getStatusBadge(attempt.status)}</td>
                                      <td className="py-2">
                                        <span className={`font-medium ${aPct >= 50 ? 'text-green-600' : 'text-red-600'}`}>
                                          {attempt.score || 0}
                                        </span>
                                        <span className="text-[#412402]/40"> / {exam?.total_points}</span>
                                        <span className={`ml-1 text-xs px-1.5 rounded-full ${aPct >= 50 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                          {aPct}%
                                        </span>
                                      </td>
                                      <td className="py-2">
                                        {attempt.violation_count > 0
                                          ? <Badge variant="destructive" className="text-xs">{attempt.violation_count}</Badge>
                                          : <span className="text-[#412402]/40">—</span>}
                                      </td>
                                      <td className="py-2 text-xs text-[#6B4C2A]">
                                        {new Date(attempt.started_at).toLocaleDateString('ar-SA')}
                                      </td>
                                      <td className="py-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-xs h-7"
                                          onClick={(e) => { e.stopPropagation(); setDetailAttempt({ ...attempt, student_name: group.student_name }); }}
                                          data-testid={`view-attempt-detail-${attempt.id}`}
                                        >
                                          تفاصيل
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {detailAttempt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">{detailAttempt.student_name}</CardTitle>
                  <p className="text-xs text-[#412402]/60 mt-1">
                    المحاولة #{detailAttempt.attempt_number || 1} • {getStatusBadge(detailAttempt.status)}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setDetailAttempt(null)}>إغلاق</Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1 py-4 space-y-4">
              {/* Score summary */}
              <div className="bg-[#FAF6EC] rounded-lg p-3 grid grid-cols-3 gap-2 text-center text-sm">
                <div>
                  <div className="text-[#412402]/60 text-xs">الدرجة</div>
                  <div className="font-bold text-[#412402]">{detailAttempt.score ?? 0} / {exam?.total_points ?? 0}</div>
                </div>
                <div>
                  <div className="text-[#412402]/60 text-xs">المخالفات</div>
                  <div className="font-bold text-red-600">{detailAttempt.violation_count || 0}</div>
                </div>
                <div>
                  <div className="text-[#412402]/60 text-xs">التاريخ</div>
                  <div className="font-medium text-[#412402]">{detailAttempt.started_at ? new Date(detailAttempt.started_at).toLocaleString('ar-SA') : '-'}</div>
                </div>
              </div>

              {/* Violations */}
              {(detailAttempt.violations || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#412402] mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4 text-red-500" /> المخالفات
                  </h4>
                  <div className="space-y-1">
                    {(detailAttempt.violations || []).map((v, i) => (
                      <div key={v.id || i} className="text-xs bg-red-50 border border-red-100 rounded p-2 flex items-center justify-between">
                        <span>#{v.violation_number || i + 1} — {v.violation_type}</span>
                        <span className="text-[#412402]/50">{v.timestamp ? new Date(v.timestamp).toLocaleString('ar-SA') : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Answers */}
              <div>
                <h4 className="text-sm font-semibold text-[#412402] mb-2">الإجابات</h4>
                {(() => {
                  const ansMap = {};
                  (detailAttempt.answers || []).forEach(a => { ansMap[a.question_id] = a.answer; });
                  const questions = exam?.questions || [];
                  if (questions.length === 0) return <p className="text-xs text-[#412402]/50">لا توجد بيانات أسئلة</p>;
                  return (
                    <div className="space-y-3">
                      {questions.map((q, idx) => {
                        const studentAnswer = ansMap[q.id];
                        let studentAnswerText = studentAnswer;
                        let correctAnswerText = q.correct_answer;
                        if (q.question_type === 'multiple_choice' && q.options) {
                          const so = q.options.find(o => o.id === studentAnswer);
                          if (so) studentAnswerText = so.text;
                          const co = q.options.find(o => o.id === q.correct_answer);
                          if (co) correctAnswerText = co.text;
                        }
                        const isCorrect = studentAnswer && (studentAnswer === q.correct_answer);
                        return (
                          <div key={q.id} className="border border-[#E8DFCF] rounded-lg p-3 bg-white">
                            <div className="flex items-start justify-between mb-1">
                              <p className="text-sm font-medium text-[#412402] whitespace-pre-wrap" dangerouslySetInnerHTML={renderSafeHtml(`#${idx + 1}. ${q.text || ''}`)} />
                              {studentAnswer === undefined ? (
                                <Badge variant="outline" className="text-xs">لم يجب</Badge>
                              ) : isCorrect ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">صحيح</Badge>
                              ) : (
                                <Badge variant="destructive" className="text-xs">خطأ</Badge>
                              )}
                            </div>
                            <div className="text-xs space-y-1 mt-2">
                              <div className="text-[#412402]/70">
                                <span className="font-semibold">إجابة الطالب: </span>
                                <span>{studentAnswerText !== undefined && studentAnswerText !== '' ? studentAnswerText : '—'}</span>
                              </div>
                              {q.correct_answer !== undefined && q.correct_answer !== '' && (
                                <div className="text-green-700">
                                  <span className="font-semibold">الإجابة الصحيحة: </span>
                                  <span>{correctAnswerText}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ExamResults;
